<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{ name: string; size?: number; strokeWidth?: number }>(), {
  size: 18,
  strokeWidth: 1.8,
})

const PATHS: Record<string, string[]> = {
  dashboard: ['M4 13h6V4H4v9Z', 'M14 20h6v-9h-6v9Z', 'M4 20h6v-3H4v3Z', 'M14 7h6V4h-6v3Z'],
  users: ['M16 20v-1.8a3.6 3.6 0 0 0-3.6-3.6H7.6A3.6 3.6 0 0 0 4 18.2V20', 'M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z', 'M20 20v-1.8a3.6 3.6 0 0 0-2.7-3.5', 'M15.5 4.3a3.5 3.5 0 0 1 0 6.8'],
  family: ['M12 20s-7-4.1-7-9.1A4 4 0 0 1 12 8a4 4 0 0 1 7 2.9c0 5-7 9.1-7 9.1Z', 'M12 8.1V5.4', 'M9.6 4.2h4.8'],
  region: ['M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z', 'M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z'],
  pill: ['M10.5 20.5 3.5 13.5a5 5 0 0 1 7-7l7 7a5 5 0 0 1-7 7Z', 'M8 8l8 8'],
  records: ['M7 3.5h7l4 4V20a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4.5a1 1 0 0 1 1-1Z', 'M14 3.5V8h4', 'M9 12h6', 'M9 16h6'],
  heart: ['M20.4 5.6a5.1 5.1 0 0 0-7.2 0L12 6.8l-1.2-1.2a5.1 5.1 0 0 0-7.2 7.2L12 21l8.4-8.2a5.1 5.1 0 0 0 0-7.2Z', 'M3.8 12h4l1.7-3.4L12.5 15l2-5 1.2 2h4.5'],
  alert: ['M12 3 2.8 20h18.4L12 3Z', 'M12 9v5', 'M12 17.2h.01'],
  rules: ['M4 6h16', 'M4 12h10', 'M4 18h6', 'M17 15.5v5', 'M14.5 18h5'],
  report: ['M5 3.5h14v17H5v-17Z', 'M8.5 8h7', 'M8.5 12h7', 'M8.5 16h4', 'M15 3.5V7h4'],
  team: ['M9 11a3.2 3.2 0 1 0 0-6.4A3.2 3.2 0 0 0 9 11Z', 'M3.5 20v-1.6A4.4 4.4 0 0 1 7.9 14h2.2a4.4 4.4 0 0 1 4.4 4.4V20', 'M16 11a3.2 3.2 0 1 0 0-6.4', 'M17.5 14h.8a3.2 3.2 0 0 1 3.2 3.2V20'],
  device: ['M8 3.5h8a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1Z', 'M11 17.5h2', 'M9.5 7h5'],
  drug: ['M7 4h10v5H7V4Z', 'M8 9h8v11H8V9Z', 'M11 12.5h2', 'M12 11.5v2', 'M9 4V2.8h6V4'],
  shield: ['M12 3 5 6v5.5c0 4.4 3 7.8 7 9.5 4-1.7 7-5.1 7-9.5V6l-7-3Z', 'M9.2 12l2 2 3.8-4'],
  bell: ['M18 9a6 6 0 1 0-12 0c0 6-2.5 7-2.5 7h17S18 15 18 9Z', 'M10 20h4'],
  template: ['M4 4h16v4H4V4Z', 'M4 10h7v10H4V10Z', 'M13 10h7v10h-7V10Z'],
  leaf: ['M20 4C10 4 4 9 4 16c0 2 1 3 3 3 7 0 12-6 13-15Z', 'M4.5 20.5C7 15 11 11 16 9'],
  help: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M9.7 9a2.4 2.4 0 1 1 3.6 2.1c-.8.5-1.3 1-1.3 2', 'M12 17h.01'],
  crown: ['M4 18h16', 'M4 18 2.8 7l5.2 4L12 4l4 7 5.2-4L20 18H4Z'],
  order: ['M6 3.5h12v17H6v-17Z', 'M9 3.5V2h6v1.5', 'M9 8h6', 'M9 12h6', 'M9 16h3'],
  decoration: ['M4 4h16v16H4V4Z', 'M4 9h16', 'M9 9v11', 'M13 13h4', 'M13 16h3'],
  link: ['M10 13.5a4 4 0 0 0 5.7 0l2.8-2.8a4 4 0 0 0-5.7-5.7L11.4 6.4', 'M14 10.5a4 4 0 0 0-5.7 0L5.5 13.3a4 4 0 0 0 5.7 5.7l1.4-1.4'],
  image: ['M4 4h16v16H4V4Z', 'M8.5 10a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z', 'M20 16l-5-5L6 20'],
  ticket: ['M4 7h16v3a2.5 2.5 0 0 0 0 4v3H4v-3a2.5 2.5 0 0 0 0-4V7Z', 'M14 7v10', 'M9 10h2', 'M9 14h2'],
  feedback: ['M4 5h16v11H9l-5 4V5Z', 'M8 9h8', 'M8 12h5'],
  chart: ['M4 20V4', 'M4 20h16', 'M8 16v-4', 'M12 16V7', 'M16 16v-6', 'M20 16v-2'],
  lock: ['M6 10h12v10H6V10Z', 'M8.5 10V7.5a3.5 3.5 0 1 1 7 0V10', 'M12 14v2.5'],
  audit: ['M6 3.5h9l4 4V20H6V3.5Z', 'M14 3.5V8h4', 'M9 12h6', 'M9 15.5h6', 'M9 19h3'],
  settings: ['M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z', 'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.1 2.1-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6V20h-3v-.2a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L6.6 16.5l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.6-1H5v-3h.4a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1 2.1-2.1.1.1a1.7 1.7 0 0 0 1.9.3 1.7 1.7 0 0 0 1-1.6V4h3v.2a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1 2.1 2.1-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.4v3h-.4a1.7 1.7 0 0 0-1.6 1Z'],
  monitor: ['M3 5h18v11H3V5Z', 'M9 20h6', 'M12 16v4', 'M7 9h2', 'M11 9h6', 'M7 12h5'],
  user: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M4.5 21a7.5 7.5 0 0 1 15 0'],
  login: ['M14 4h5v16h-5', 'M10 8l4 4-4 4', 'M3 12h11'],
  compass: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M15.5 8.5l-2 5-5 2 2-5 5-2Z'],
  'wifi-off': ['M2 3l19 19', 'M8.5 15.5a5 5 0 0 1 7 0', 'M5 12a10 10 0 0 1 4-2.4', 'M15 9.6A10 10 0 0 1 19 12', 'M12 19h.01', 'M2 9a15 15 0 0 1 4-2.6', 'M18 6.4A15 15 0 0 1 22 9'],
  wrench: ['M14.5 6.5a4.5 4.5 0 0 0-6 6L4 17v3h3l4.5-4.5a4.5 4.5 0 0 0 6-6l-2.5 2.5-2.5-2.5 2.5-2.5Z'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z', 'M20 20l-4-4'],
  plus: ['M12 5v14', 'M5 12h14'],
  close: ['M6 6l12 12', 'M18 6 6 18'],
  more: ['M5 12h.01', 'M12 12h.01', 'M19 12h.01'],
  refresh: ['M20 11a8 8 0 0 0-14.8-4.4L4 8', 'M4 4v4h4', 'M4 13a8 8 0 0 0 14.8 4.4L20 16', 'M20 20v-4h-4'],
  filter: ['M3 5h18l-7 8v6l-4 2v-8L3 5Z'],
  download: ['M12 4v11', 'M8 11l4 4 4-4', 'M4 20h16'],
  upload: ['M12 20V9', 'M8 13l4-4 4 4', 'M4 4h16'],
  check: ['M5 12.5l4.5 4.5L19 7'],
  chevron: ['M9 6l6 6-6 6'],
  layers: ['M12 3 3 8l9 5 9-5-9-5Z', 'M3 12l9 5 9-5', 'M3 16l9 5 9-5'],
  tag: ['M4 4h7l9 9-7 7-9-9V4Z', 'M8.5 8.5h.01'],
  gift: ['M4 9h16v11H4V9Z', 'M3 5h18v4H3V5Z', 'M12 5v15', 'M12 5C10 5 8 4 8 2.8 8 1.8 9 1 10 1c1.5 0 2 2 2 4Z', 'M12 5c2 0 4-1 4-2.2 0-1-1-1.8-2-1.8-1.5 0-2 2-2 4Z'],
  scan: ['M4 8V4h4', 'M20 8V4h-4', 'M4 16v4h4', 'M20 16v4h-4', 'M7 12h10'],
  calendar: ['M4 6h16v14H4V6Z', 'M8 3v5', 'M16 3v5', 'M4 10h16'],
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 7v5l3 2'],
  eye: ['M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z', 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z'],
  trash: ['M4 7h16', 'M9 7V4h6v3', 'M6 7l1 14h10l1-14', 'M10 11v6', 'M14 11v6'],
  edit: ['M4 20h4L19 9l-4-4L4 16v4Z', 'M14 6l4 4'],
  warning: ['M12 3 2.8 20h18.4L12 3Z', 'M12 9v5', 'M12 17.2h.01'],
  info: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 11v5', 'M12 8h.01'],
  logout: ['M10 4H5v16h5', 'M14 8l4 4-4 4', 'M9 12h9'],
  key: ['M15 9a4.5 4.5 0 1 0-4.2 4.5L9 15.5H7v2H5v2H2v-3l7.3-7.3A4.5 4.5 0 0 1 15 9Z', 'M16.5 7.5h.01'],
  smartphone: ['M8 3h8v18H8V3Z', 'M11 18h2'],
  server: ['M4 4h16v6H4V4Z', 'M4 14h16v6H4v-6Z', 'M7 7h.01', 'M7 17h.01'],
  database: ['M12 3c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3Z', 'M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6', 'M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3'],
  activity: ['M3 12h4l2-6 4 12 2-6h6'],
  sparkles: ['M12 3l1.4 4.1L17.5 8.5l-4.1 1.4L12 14l-1.4-4.1L6.5 8.5l4.1-1.4L12 3Z', 'M18.5 14l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z'],
  menu: ['M4 6h16', 'M4 12h16', 'M4 18h16'],
  home: ['M3 11 12 3l9 8', 'M5 10v11h14V10', 'M10 21v-6h4v6'],
  external: ['M14 4h6v6', 'M20 4 11 13', 'M18 14v6H4V6h6'],
  copy: ['M9 9h11v11H9V9Z', 'M15 9V4H4v11h5'],
  play: ['M8 5l11 7-11 7V5Z'],
  pause: ['M8 5v14', 'M16 5v14'],
  drag: ['M9 5h.01', 'M15 5h.01', 'M9 12h.01', 'M15 12h.01', 'M9 19h.01', 'M15 19h.01'],
  'arrow-left': ['M19 12H5', 'M11 6l-6 6 6 6'],
  'arrow-right': ['M5 12h14', 'M13 6l6 6-6 6'],
  'chevron-down': ['M6 9l6 6 6-6'],
  'check-circle': ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M8 12l2.5 2.5L16 9'],
}

const paths = computed(() => PATHS[props.name] ?? PATHS.info ?? [])
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path v-for="(path, index) in paths" :key="index" :d="path" />
  </svg>
</template>
